import { useState, useEffect } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import { 
  Bell, 
  Send, 
  Clock, 
  User, 
  Phone, 
  Mail,
  MessageCircle,
  Settings,
  History,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  RefreshCw,
  Filter,
  Calendar,
  TrendingUp,
  Users
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  high: { label: "Alta", color: "text-red-600", bg: "bg-red-100", border: "border-red-200" },
  medium: { label: "Media", color: "text-amber-600", bg: "bg-amber-100", border: "border-amber-200" },
  low: { label: "Baja", color: "text-slate-600", bg: "bg-slate-100", border: "border-slate-200" }
};

const REASON_LABELS = {
  inactivity: "Inactividad",
  trial_expiring: "Trial por vencer",
  upcoming_interview: "Entrevista próxima",
  documents_pending: "Docs pendientes"
};

const AdminReminders = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("pending");
  const [pendingReminders, setPendingReminders] = useState([]);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState({
    enabled: true,
    inactive_days_soft: 3,
    inactive_days_urgent: 7,
    trial_warning_days: 2,
    max_reminders_per_week: 2
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, historyRes, templatesRes, configRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/reminders/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/reminders/history?limit=30`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/reminders/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/reminders/config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/reminders/stats?days=30`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setPendingReminders(pendingRes.data.pending || []);
      setReminderHistory(historyRes.data.reminders || []);
      setTemplates(templatesRes.data.templates || []);
      setConfig(configRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (reminder) => {
    setSendingReminder(reminder.user.id);
    try {
      const response = await axios.post(`${API}/api/reminders/send`, {
        user_id: reminder.user.id,
        template_key: reminder.suggested_template,
        channel: "whatsapp"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open WhatsApp link
      if (response.data.reminder?.whatsapp_link) {
        window.open(response.data.reminder.whatsapp_link, "_blank");
      }
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error("Error sending reminder:", error);
    } finally {
      setSendingReminder(null);
    }
  };

  const handleMarkSent = async (reminderId) => {
    try {
      await axios.post(`${API}/api/reminders/mark-sent/${reminderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error("Error marking sent:", error);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API}/api/reminders/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowConfig(false);
    } catch (error) {
      console.error("Error saving config:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-reminders-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-navy-900">
              Recordatorios Automáticos
            </h1>
            <p className="text-navy-500">Gestiona los recordatorios para clientes inactivos</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowConfig(!showConfig)}
              data-testid="config-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </Button>
            <Button onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-navy-100 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Send className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-navy-500">Enviados (30d)</p>
                  <p className="text-2xl font-bold text-navy-900">{stats.total_reminders || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-navy-100 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-navy-500">Pendientes</p>
                  <p className="text-2xl font-bold text-navy-900">{pendingReminders.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-navy-100 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-navy-500">Prioridad Alta</p>
                  <p className="text-2xl font-bold text-navy-900">
                    {pendingReminders.filter(r => r.priority === 'high').length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-navy-100 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-navy-500">Por Inactividad</p>
                  <p className="text-2xl font-bold text-navy-900">
                    {stats.by_type?.inactive_3_days || 0 + stats.by_type?.inactive_7_days || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Configuration Panel */}
        {showConfig && (
          <div className="bg-white rounded-xl border border-navy-100 p-6">
            <h3 className="font-semibold text-navy-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuración de Recordatorios
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-navy-600 mb-1">Días para recordatorio suave</label>
                <input
                  type="number"
                  value={config.inactive_days_soft}
                  onChange={(e) => setConfig({...config, inactive_days_soft: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg"
                  min={1}
                  max={30}
                />
              </div>
              <div>
                <label className="block text-sm text-navy-600 mb-1">Días para recordatorio urgente</label>
                <input
                  type="number"
                  value={config.inactive_days_urgent}
                  onChange={(e) => setConfig({...config, inactive_days_urgent: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg"
                  min={1}
                  max={60}
                />
              </div>
              <div>
                <label className="block text-sm text-navy-600 mb-1">Días aviso trial</label>
                <input
                  type="number"
                  value={config.trial_warning_days}
                  onChange={(e) => setConfig({...config, trial_warning_days: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg"
                  min={1}
                  max={7}
                />
              </div>
              <div>
                <label className="block text-sm text-navy-600 mb-1">Max recordatorios/semana</label>
                <input
                  type="number"
                  value={config.max_reminders_per_week}
                  onChange={(e) => setConfig({...config, max_reminders_per_week: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg"
                  min={1}
                  max={7}
                />
              </div>
            </div>
            <div className="flex justify-end mt-4 gap-2">
              <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
              <Button onClick={handleSaveConfig} className="bg-emerald-600 hover:bg-emerald-700">
                Guardar Configuración
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-navy-100">
          <button
            onClick={() => setActiveTab("pending")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "pending" 
                ? "text-emerald-600 border-b-2 border-emerald-600" 
                : "text-navy-500 hover:text-navy-700"
            }`}
          >
            <Bell className="w-4 h-4 inline mr-2" />
            Pendientes ({pendingReminders.length})
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "history" 
                ? "text-emerald-600 border-b-2 border-emerald-600" 
                : "text-navy-500 hover:text-navy-700"
            }`}
          >
            <History className="w-4 h-4 inline mr-2" />
            Historial
          </button>
          <button
            onClick={() => setActiveTab("templates")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "templates" 
                ? "text-emerald-600 border-b-2 border-emerald-600" 
                : "text-navy-500 hover:text-navy-700"
            }`}
          >
            <MessageCircle className="w-4 h-4 inline mr-2" />
            Plantillas
          </button>
        </div>

        {/* Content */}
        {activeTab === "pending" && (
          <div className="space-y-4">
            {pendingReminders.length === 0 ? (
              <div className="bg-white rounded-xl border border-navy-100 p-12 text-center">
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-navy-900 mb-2">¡Todo al día!</h3>
                <p className="text-navy-500">No hay recordatorios pendientes por enviar</p>
              </div>
            ) : (
              pendingReminders.map((reminder, idx) => {
                const priorityConfig = PRIORITY_CONFIG[reminder.priority] || PRIORITY_CONFIG.low;
                return (
                  <div 
                    key={idx}
                    className={`bg-white rounded-xl border ${priorityConfig.border} p-4`}
                    data-testid={`pending-reminder-${idx}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-full ${priorityConfig.bg} flex items-center justify-center flex-shrink-0`}>
                          <User className={`w-6 h-6 ${priorityConfig.color}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-navy-900">{reminder.user.name}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${priorityConfig.bg} ${priorityConfig.color}`}>
                              {priorityConfig.label}
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                              {REASON_LABELS[reminder.reason] || reminder.reason}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-navy-500">
                            {reminder.user.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {reminder.user.email}
                              </span>
                            )}
                            {reminder.user.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {reminder.user.phone}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 text-sm">
                            {reminder.reason === "inactivity" && (
                              <span className="text-amber-600">
                                <Clock className="w-4 h-4 inline mr-1" />
                                {reminder.days_inactive} días sin actividad
                              </span>
                            )}
                            {reminder.reason === "trial_expiring" && (
                              <span className="text-red-600">
                                <AlertTriangle className="w-4 h-4 inline mr-1" />
                                Trial vence en {reminder.days_left} días
                              </span>
                            )}
                            {reminder.reason === "upcoming_interview" && (
                              <span className="text-blue-600">
                                <Calendar className="w-4 h-4 inline mr-1" />
                                Entrevista en {reminder.days_until} días
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-16 lg:ml-0">
                        <Button
                          onClick={() => handleSendReminder(reminder)}
                          disabled={sendingReminder === reminder.user.id}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`send-reminder-${idx}`}
                        >
                          {sendingReminder === reminder.user.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <MessageCircle className="w-4 h-4 mr-2" />
                          )}
                          Enviar WhatsApp
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === "history" && (
          <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-navy-100">
                <tr>
                  <th className="text-left p-4 font-medium text-navy-600">Usuario</th>
                  <th className="text-left p-4 font-medium text-navy-600">Tipo</th>
                  <th className="text-left p-4 font-medium text-navy-600">Canal</th>
                  <th className="text-left p-4 font-medium text-navy-600">Estado</th>
                  <th className="text-left p-4 font-medium text-navy-600">Fecha</th>
                  <th className="text-left p-4 font-medium text-navy-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {reminderHistory.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-navy-500">
                      No hay historial de recordatorios
                    </td>
                  </tr>
                ) : (
                  reminderHistory.map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-navy-900">{item.user_name}</p>
                          <p className="text-sm text-navy-500">{item.user_phone}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-600">
                          {item.reminder_type?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="flex items-center gap-1 text-sm text-navy-600">
                          <MessageCircle className="w-4 h-4" />
                          {item.channel}
                        </span>
                      </td>
                      <td className="p-4">
                        {item.status === "sent" ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Enviado
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-amber-600 text-sm">
                            <Clock className="w-4 h-4" />
                            Pendiente
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-navy-600">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="p-4">
                        {item.status !== "sent" && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleMarkSent(item.id)}
                          >
                            Marcar enviado
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "templates" && (
          <div className="grid md:grid-cols-2 gap-4">
            {templates.map((template, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-navy-100 p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-navy-900">{template.title}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    PRIORITY_CONFIG[template.priority]?.bg || 'bg-slate-100'
                  } ${PRIORITY_CONFIG[template.priority]?.color || 'text-slate-600'}`}>
                    {PRIORITY_CONFIG[template.priority]?.label || template.priority}
                  </span>
                </div>
                <p className="text-sm text-navy-500 mb-2">
                  <code className="bg-slate-100 px-1 rounded">{template.key}</code>
                </p>
                <p className="text-sm text-navy-600 line-clamp-3">{template.message_preview}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminReminders;
